/**
 * Zone Signature Orchestra — per-volume spectral identity for Ops Live fences.
 * Soft≠Live: Live rings only. Soft pads stay copper-weak.
 *
 * Signature: each zone owns hue + harmonic + BPM + dash signature so
 * adjacent volumes never blend into one wash.
 */
export const ZONE_SPECTRUM = Object.freeze([
  { tone: "phosphor", color: "#7CFFB2", harmonic: "#B8F0FF", bpm: 11 },
  { tone: "copper", color: "#E8A86A", harmonic: "#FFD9A8", bpm: 14 },
  { tone: "ice", color: "#6ED8FF", harmonic: "#C8F0FF", bpm: 9 },
  { tone: "coral", color: "#FF6B5A", harmonic: "#FFB4A8", bpm: 8 },
  { tone: "citron", color: "#D4F06A", harmonic: "#F0FFB0", bpm: 13 },
  { tone: "orchid", color: "#D48CFF", harmonic: "#F0D0FF", bpm: 10 },
  { tone: "tide", color: "#3DE1C8", harmonic: "#9FE8D8", bpm: 12 },
  { tone: "ember", color: "#FF9F43", harmonic: "#FFD0A0", bpm: 7 },
  { tone: "steel", color: "#9AB0C4", harmonic: "#D0DCE8", bpm: 15 },
]);

export function hashZoneSlot(zoneId) {
  const s = String(zoneId || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h) % ZONE_SPECTRUM.length;
}

export function zoneSpectrumOf(zoneId) {
  return ZONE_SPECTRUM[hashZoneSlot(zoneId)] || ZONE_SPECTRUM[0];
}

/** Morse-ish dash pattern unique per zone (MapLibre line-dasharray units). */
export function zoneDashSignatureOf(zoneId) {
  const h = Math.abs(
    String(zoneId || "")
      .split("")
      .reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0),
  );
  const a = 1.2 + (h % 5) * 0.55;
  const b = 0.6 + ((h >> 3) % 4) * 0.45;
  const c = 2.0 + ((h >> 6) % 3) * 0.8;
  const d = 0.8 + ((h >> 9) % 3) * 0.5;
  return [a, b, c, d];
}

export function enrichFencePulseProps(base) {
  const zoneId = String((base && base.zoneId) || (base && base.name) || "").trim();
  const soft = Number((base && base.soft) || 0) === 1 ? 1 : 0;
  const spec = soft
    ? { tone: "copper", color: "#E8A86A", harmonic: "#FFD9A8", bpm: 18 }
    : zoneSpectrumOf(zoneId);
  const dash = soft ? [2.4, 2.2] : zoneDashSignatureOf(zoneId);
  return {
    ...(base || {}),
    zoneId,
    name: (base && base.name) || zoneId,
    soft,
    tone: spec.tone,
    color: spec.color,
    harmonic: spec.harmonic,
    bpm: spec.bpm,
    dashA: dash[0],
    dashB: dash[1],
    dashC: dash[2] || 1.5,
    dashD: dash[3] || 0.8,
    pulseSlot: hashZoneSlot(zoneId),
    pulseLabel: soft ? "Practice pad" : "Site signature",
  };
}

export function fenceFillColorExpr() {
  return [
    "case",
    ["==", ["get", "soft"], 1], "#E8A86A",
    ["get", "color"],
  ];
}

export function fenceLineColorExpr() {
  return [
    "case",
    ["==", ["get", "soft"], 1], "#FFD9A8",
    ["get", "color"],
  ];
}

/**
 * Dual-state pulse: `pulse` (opacity breath) + `rib` (ribbon width breath).
 * Each zone uses its own BPM so the orchestra never locks in phase.
 */
export function tickZonePulseStates(map, zoneMeta, step) {
  if (!map || !Array.isArray(zoneMeta) || !zoneMeta.length) return;
  for (const z of zoneMeta) {
    const id = z && (z.id || z.zoneId);
    if (!id) continue;
    const bpm = Math.max(6, Number(z.bpm) || zoneSpectrumOf(id).bpm);
    const slot = hashZoneSlot(id);
    const local = (step + slot * 5) % bpm;
    const rise = local / Math.max(1, bpm - 1);
    const pulse = 0.32 + Math.sin(rise * Math.PI) * 0.58;
    const rib = 0.4 + Math.sin((rise + 0.25) * Math.PI * 2) * 0.35;
    try {
      map.setFeatureState({ source: "fence", id: String(id) }, { pulse, rib });
    } catch {
      /* */
    }
  }
}

/** Centroids as beacon points — unique color identity at volume heart. */
export function buildZoneBeacons(fenceFeatures) {
  const features = [];
  for (const f of fenceFeatures || []) {
    if (!f || !f.properties || Number(f.properties.soft) === 1) continue;
    const ring =
      f.geometry && f.geometry.type === "Polygon" && f.geometry.coordinates
        ? f.geometry.coordinates[0]
        : null;
    if (!Array.isArray(ring) || ring.length < 3) continue;
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (const p of ring) {
      if (!Array.isArray(p) || p.length < 2) continue;
      sx += Number(p[0]);
      sy += Number(p[1]);
      n += 1;
    }
    if (!n) continue;
    features.push({
      type: "Feature",
      properties: {
        zoneId: f.properties.zoneId,
        name: f.properties.name,
        color: f.properties.color,
        harmonic: f.properties.harmonic,
        tone: f.properties.tone,
        soft_ne_live: false,
      },
      geometry: { type: "Point", coordinates: [sx / n, sy / n] },
    });
  }
  return { type: "FeatureCollection", features };
}

export default {
  ZONE_SPECTRUM,
  hashZoneSlot,
  zoneSpectrumOf,
  zoneDashSignatureOf,
  enrichFencePulseProps,
  fenceFillColorExpr,
  fenceLineColorExpr,
  tickZonePulseStates,
  buildZoneBeacons,
};
